import { computed } from 'vue';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';

import { useMetricStore } from '@/store/metric/metric';
import { useDataAndGroupingStore } from '@/store/metric/dataAndGrouping';
import { useMetricDataStore } from '../store/metric/metricData';
import { useAssessmentStore } from '../products/pha/modules/assessment/store';

export const useMetricComposable = () => {
    const store = useStore();
    const metricStore = useMetricStore();
    const toast = useToast();
    const confirm = useConfirm();
    const dataAndGrouping = useDataAndGroupingStore();

    const company_id = computed(() => {
        return store.getters['auth/userCompanyId']?.id;
    });

    // user-group store variables
    const { metric, editMetric, metrics, allDSMetrics, companyPools, totalRecords, attributesGroupedByBlocks } = storeToRefs(metricStore);

    // user-group store methods
    const { createMetric, fetchAllDSMetrics, fetchAllCompanyPools, fetchMetricDetails, updateMetric, deleteMetric, initMetric, flushMetricData } = metricStore;

    // assessment store methods
    const assessmentStore = useAssessmentStore();

    // metricData store variables
    const { metricReportData } = storeToRefs(useMetricDataStore());

    // metricData store methods
    const { getMetricData } = useMetricDataStore();

    const getAllMetrics = async (params) => {
        // await fetchAllMetrics({ ...params, company_id: company_id.value }); // this is for old metrics
        await fetchAllDSMetrics({ ...params, company_id: company_id.value }); // this is for datasource metrics
    };

    const getAllCompanyPools = async () => {
        await fetchAllCompanyPools(company_id.value);
    };

    const getMetricDetailsById = async (id) => {
        return await fetchMetricDetails(company_id.value, id);
    };

    const addNewMetric = async (params) => {
        return await createMetric({ ...params, company_id: company_id.value });
    };

    const updateMetricDetails = async (id, body) => {
        return await updateMetric(id, { ...body, company_id: company_id.value });
    };

    const removeMetric = async (id) => {
        return await deleteMetric(id, company_id.value);
    };

    // loads attributes by block taking assessment_code as a parameter
    const loadAttributesByBlock = async (metric_type, assessment_code, currentKey) => {
        // check if existing attributesGroupedByBlocks already have assessment_code related attributes
        const existingAttributesByBlocks = attributesGroupedByBlocks.value?.[metric_type]?.find((obj) => obj?.assessment_code === assessment_code);
        // if found then assign its info to the key of the array orelse ignore
        if (existingAttributesByBlocks) {
            attributesGroupedByBlocks.value[metric_type][currentKey] = existingAttributesByBlocks;
        } else {
            // else find again
            // use the currentKey to appoint the attributesByBlocks to the exact index options of the currentKey
            // const attributes = await getAttributesByBlock(assessment_code);
            const attributes = await assessmentStore.getAttributesGroupByBlocks(assessment_code);
            attributesGroupedByBlocks.value[metric_type][currentKey] = attributes;
        }
    };

    // type is either metricData or metricGrouping
    const confirmRemove = (type, event, index) => {
        return confirm.require({
            target: event.currentTarget,
            group: 'confirmPopup',
            message: 'Do you want to remove this field?',
            icon: 'pi pi-info-circle',
            acceptClass: 'p-button-danger',
            accept: async () => {
                if (type === 'metricData') {
                    await dataAndGrouping.removeMetricData(index);
                } else {
                    await dataAndGrouping.removeMetricGrouping(index);
                }
                toast.add({ severity: 'info', summary: 'Confirmed', detail: 'Field removed', life: 5000 });
            },
            reject: () => {
                toast.add({ severity: 'error', summary: 'Rejected', detail: 'Field not removed', life: 5000 });
            },
        });
    };

    const loadMetricReportData = async (metric_id) => {
        return await getMetricData(company_id.value, metric_id);
    };

    return {
        // metric store states
        metric,
        editMetric,
        metrics,
        allDSMetrics,
        companyPools,
        totalRecords,
        attributesGroupedByBlocks,

        // metricData store states
        metricReportData,

        // metric store methods
        getAllMetrics,
        getAllCompanyPools,
        getMetricDetailsById,
        addNewMetric,
        updateMetricDetails,
        removeMetric,
        initMetric,
        flushMetricData,

        // composable methods
        loadAttributesByBlock,
        confirmRemove,
        loadMetricReportData, // metric data store
    };
};
